/**
 * 動画の再生位置をそろえる(#123 / #159 で共有へ移した)。
 *
 * 正本は [#36](../../../docs/issues/36-s04-editor/DESIGN.md) の
 * 「テスト動画プレビュー」。
 *
 * ## なぜ `packages/shared` にあるのか
 *
 * **S04 とビューアの両方が使う**(#159)。#123 では S04 だけが持っていたが、
 * ビューアにも再生 / 停止 / 同期を付けるにあたり、**同じ規則の実装を
 * 2 つ作らない**ためにここへ移した。
 *
 * **DOM に触れないので shared に置ける** —— 扱うのは
 * `{ currentTime, duration }` という形だけで、`<video>` そのものは知らない。
 *
 * ## なぜ壁時計を基準にするのか
 *
 * **`<video>` どうしを直接比べられない。** 動画ごとに長さが違うと、
 * ループを跨いだ瞬間に `currentTime` が巻き戻り、**同じ瞬間なのに
 * 大きくずれて見える**(10 秒の動画が 1 秒、30 秒の動画が 11 秒)。
 *
 * そこで**「同期を押した瞬間からの経過時間」という 1 本の時間軸**を持ち、
 * 各動画のあるべき位置を `経過 mod 長さ` で出す。長さが違っても正しい。
 *
 * ## 定期的には直さない(#172 で撤回)
 *
 * 以前は 2 秒ごとにずれを見張って直していた。**やめた** ——
 * 利用者の判断:「最初をそろえてスタートすればあとは何もしなくても問題ない」。
 *
 * **直す手段が、ずれの原因を悪化させていた。**
 * `currentTime` への代入は**シーク**で、圧縮された動画では
 * **目標より前のキーフレームまで戻って復号し直す。** 通常の再生より重い。
 *
 * 復号が追いつかずに遅れている動画をシークで直すと、**さらに遅れる** ——
 * 2 秒後にまた補正され、**遅れている動画だけが繰り返しシークされる。**
 * これは「一部の動画だけ進みが遅い」([#168](https://github.com/silver-i-development/signa-preview-2d/issues/168))
 * の形と一致する。
 *
 * **いま `correctionsFor` を使うのは 1 か所だけ** ——
 * 再生中に素材を当てたときの**合流の位置合わせ**(1 回のシーク)。
 * ずれが気になったら**利用者が「同期」を押す。**
 *
 * **すべて純粋関数。** 実際の `<video>` を使わずに検査できる。
 */
/**
 * 直す基準になるずれ(秒)。
 *
 * **0.15 秒。** これより細かいずれは並べて見ても分からず、直すと
 * 飛びのほうが目立つ。**30fps で約 4〜5 コマ。**
 */
export const SYNC_TOLERANCE = 0.15;
/** 止まった状態の時間軸。 */
export const STOPPED_TIMELINE = { baseTime: 0, startedAt: null };
/**
 * 動かし始める。
 *
 * `baseTime` を省くと**先頭から**(= 同期)。止めた位置から続けるときは
 * `positionOf` の結果を渡す。
 */
export function startTimeline(now, baseTime = 0) {
    return { baseTime, startedAt: now };
}
/** 止める。**止めた位置を覚えておく**(再生で続きから始められる)。 */
export function stopTimeline(timeline, now) {
    return { baseTime: positionOf(timeline, now), startedAt: null };
}
/** いまの位置(秒)。**止まっていれば止めた位置。** */
export function positionOf(timeline, now) {
    if (timeline.startedAt === null)
        return timeline.baseTime;
    return timeline.baseTime + (now - timeline.startedAt) / 1000;
}
/**
 * その動画があるべき時刻。
 *
 * **ループするので長さで折り返す。** 長さが取れていない
 * (`0` / `NaN` / `Infinity` —— メタデータ待ちやストリーム)ときは
 * **折り返さずそのまま返す**(補正の判定側で弾く)。
 */
export function expectedTime(position, duration) {
    if (!Number.isFinite(duration) || duration <= 0)
        return position;
    const wrapped = position % duration;
    return wrapped < 0 ? wrapped + duration : wrapped;
}
/**
 * ずれの大きさ(秒)。
 *
 * **ループを跨いだ差を正しく測る。** 長さ 10 秒の動画で 9.9 秒と 0.1 秒は
 * **9.8 秒ではなく 0.2 秒**ずれている。
 */
export function driftOf(current, expected, duration) {
    const raw = Math.abs(current - expected);
    if (!Number.isFinite(duration) || duration <= 0)
        return raw;
    return Math.min(raw, duration - raw);
}
/**
 * 直すべき動画とその行き先を出す。
 *
 * **長さが取れていないものは触らない** —— あるべき時刻が決まらないため。
 *
 * **定期的には呼ばない**(#172)。使うのは**再生中に素材を当てたときの
 * 合流**だけで、そのとき 1 回だけシークする ——
 * `currentTime` への代入は重いので、**回数を増やさない。**
 */
export function correctionsFor(videos, position, tolerance = SYNC_TOLERANCE) {
    const corrections = [];
    videos.forEach((video, index) => {
        const { duration } = video;
        if (!Number.isFinite(duration) || duration <= 0)
            return;
        const to = expectedTime(position, duration);
        const drift = driftOf(video.currentTime, to, duration);
        if (drift > tolerance)
            corrections.push({ index, to, drift });
    });
    return corrections;
}
//# sourceMappingURL=playback.js.map